(function () {
  "use strict";

  if (!window.preact || !window.preactHooks) {
    console.error("Preact and preactHooks are required for tournament modal rendering");
    return;
  }

  const { h, render } = window.preact;
  const { useEffect, useMemo, useState } = window.preactHooks;

  const DEFAULT_FORM = {
    id: "",
    title: "",
    series: "",
    locationName: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    tournamentStartDate: "",
    tournamentEndDate: "",
    ratingSystemType: "apa_skill_level",
    rulesType: "",
    buyIn: "",
    description: "",
    applyToSeries: false,
    specificationData: { tournaments: {} },
    galleryUrls: "",
    seriesEnabled: false,
    seriesCount: "1",
    seriesFrequency: "weekly",
    seriesInterval: "1",
    seriesDaysOfWeek: [],
  };

  function normalizeRegionValue(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  function normalizeRatingSystemType(value) {
    const normalized = String(value || "").trim();
    const map = {
      APA: "apa_skill_level",
      Fargo: "fargo_rate",
      BCA: "bca_rating",
      TAP: "tap_handicap",
      USAPL: "usapl_rating",
      VNEA: "vnea_rating",
      ACS: "acs_rating",
      "In-house": "in_house_handicap",
      Open: "no_handicap_open",
      Other: "other",
    };
    return map[normalized] || normalized || "apa_skill_level";
  }

  function normalizeRulesType(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    const key = raw.replace(/[\s-]+/g, "_");
    const map = {
      wepf: "wepf_world_rules",
      wepf_world_rules: "wepf_world_rules",
      world_rules: "wepf_world_rules",
      wpa_world_standardized_rules: "wpa_world_standardized_rules",
      world_standardized_rules: "wpa_world_standardized_rules",
      bca_pool_league_rules: "bca_pool_league_rules",
      bca_rules: "bca_pool_league_rules",
      apa_rules: "apa_rules",
      tap_rules: "tap_rules",
      usapl_rules: "usapl_rules",
      vnea_rules: "vnea_rules",
      house_rules: "house_rules",
      in_house_rules: "house_rules",
      other: "other",
    };
    return map[key] || key;
  }

  function datetimeLocalToIso(value) {
    if (!value) return "";
    const asDate = new Date(value);
    if (!Number.isFinite(asDate.getTime())) return "";
    return asDate.toISOString();
  }

  function normalizeOptionalEmail(value) {
    const raw = String(value || "").trim();
    return raw ? raw.toLowerCase() : "";
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ""));
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const commaIndex = result.indexOf(",");
        if (commaIndex < 0) {
          reject(new Error("Unable to read file data"));
          return;
        }
        resolve(result.slice(commaIndex + 1));
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  async function resizeImageIfNeeded(file, maxPixels) {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;

    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = (error) => {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      };
      img.src = objectUrl;
    });

    const width = image.width;
    const height = image.height;
    const totalPixels = width * height;
    if (totalPixels <= maxPixels) return file;

    const scale = Math.sqrt(maxPixels / totalPixels);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(width * scale));
    canvas.height = Math.max(1, Math.floor(height * scale));

    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, file.type || "image/jpeg", 0.9)
    );

    if (!blob) return file;
    return new File([blob], file.name, { type: file.type || "image/jpeg" });
  }

  function FrontendSharedTournamentModal(props) {
    const {
      isOpen,
      onClose,
      onSubmit,
      onLocationSearch,
      allProfiles = [],
      locationStateOptions = [],
      locationCityOptions = [],
    } = props;

    const [tournamentModalOpen, setTournamentModalOpen] = useState(Boolean(isOpen));
    const [tournamentProfileId, setTournamentProfileId] = useState("");
    const [tournamentLocationFormQuery, setTournamentLocationFormQuery] = useState("");
    const [tournamentLocationFormStateFilter, setTournamentLocationFormStateFilter] = useState("");
    const [tournamentLocationFormCityFilter, setTournamentLocationFormCityFilter] = useState("");
    const [tournamentForm, setTournamentForm] = useState({ ...DEFAULT_FORM });
    const [tournamentGalleryPreviews, setTournamentGalleryPreviews] = useState([]);
    const [tournamentGalleryFileIds, setTournamentGalleryFileIds] = useState([]);
    const [tournamentGalleryUrls, setTournamentGalleryUrls] = useState([]);
    const [tournamentPendingUploads, setTournamentPendingUploads] = useState(0);
    const [tournamentSaveStatus, setTournamentSaveStatus] = useState("");
    const [tournamentFieldErrors, setTournamentFieldErrors] = useState({});
    const [tournamentStep, setTournamentStep] = useState(1);

    const getStateAbbrev = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      const normalized = normalizeRegionValue(raw);
      const matched = (locationStateOptions || []).find(
        (option) => normalizeRegionValue(option) === normalized
      );
      return String(matched || raw).trim();
    };

    const getStateDisplayName = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      const normalized = normalizeRegionValue(raw);
      const matched = (locationStateOptions || []).find(
        (option) => normalizeRegionValue(option) === normalized
      );
      return String(matched || raw).trim();
    };

    useEffect(() => {
      setTournamentModalOpen(Boolean(isOpen));
    }, [isOpen]);

    useEffect(() => {
      if (!tournamentProfileId) return;
      const selected = (allProfiles || []).find((profile) => String(profile.id) === String(tournamentProfileId));
      if (!selected) return;
      setTournamentForm((prev) => ({ ...prev, locationName: selected.title || selected.slug || "" }));
    }, [tournamentProfileId, allProfiles]);

    const filteredProfiles = useMemo(() => {
      return (allProfiles || []).filter((profile) => {
        if (!profile) return false;
        if (tournamentProfileId && profile.id === tournamentProfileId) return true;

        if (tournamentLocationFormStateFilter) {
          const stateMatch =
            normalizeRegionValue(profile.locationState) === normalizeRegionValue(tournamentLocationFormStateFilter);
          if (!stateMatch) return false;
        }

        if (tournamentLocationFormCityFilter) {
          const cityMatch =
            String(profile.locationCity || "").toLowerCase() === String(tournamentLocationFormCityFilter || "").toLowerCase();
          if (!cityMatch) return false;
        }

        if (tournamentLocationFormQuery && tournamentLocationFormQuery.trim().length >= 1) {
          const query = tournamentLocationFormQuery.trim().toLowerCase();
          const title = String(profile.title || profile.slug || "").toLowerCase();
          if (!title.includes(query)) return false;
        }

        return true;
      });
    }, [
      allProfiles,
      tournamentProfileId,
      tournamentLocationFormStateFilter,
      tournamentLocationFormCityFilter,
      tournamentLocationFormQuery,
    ]);

    const setTournamentLocationFormQueryWithSearch = (value) => {
      const nextQuery = String(value || "");
      setTournamentLocationFormQuery(nextQuery);
      if (typeof onLocationSearch === "function") {
        onLocationSearch({
          query: nextQuery,
          state: tournamentLocationFormStateFilter,
          city: tournamentLocationFormCityFilter,
        });
      }
    };

    const setTournamentLocationFormStateFilterWithSearch = (value) => {
      const nextState = String(value || "");
      setTournamentLocationFormStateFilter(nextState);
      if (typeof onLocationSearch === "function") {
        onLocationSearch({
          query: tournamentLocationFormQuery,
          state: nextState,
          city: tournamentLocationFormCityFilter,
        });
      }
    };

    const setTournamentLocationFormCityFilterWithSearch = (value) => {
      const nextCity = String(value || "");
      setTournamentLocationFormCityFilter(nextCity);
      if (typeof onLocationSearch === "function") {
        onLocationSearch({
          query: tournamentLocationFormQuery,
          state: tournamentLocationFormStateFilter,
          city: nextCity,
        });
      }
    };

    async function handleTournamentGallerySelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const maxImages = 10;
      const maxSizeBytes = 10 * 1024 * 1024;
      const allowedMimeTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
      const currentCount = tournamentGalleryFileIds.length;
      if (currentCount + files.length > maxImages) {
        setTournamentSaveStatus("Gallery is limited to 10 images.");
        return;
      }

      for (const file of Array.from(files)) {
        setTournamentPendingUploads((prev) => prev + 1);
        let previewUrl = "";
        try {
          const uploadFile = await resizeImageIfNeeded(file, 25000000);
          const mimeType = String(uploadFile.type || "").toLowerCase() === "image/jpg"
            ? "image/jpeg"
            : String(uploadFile.type || "").toLowerCase();

          if (!allowedMimeTypes.includes(mimeType)) {
            throw new Error(`Only JPG, PNG, WEBP, and GIF are allowed. Got: ${mimeType || "unknown"}`);
          }

          if (uploadFile.size > maxSizeBytes) {
            throw new Error(`Image exceeds 10MB after processing (${(uploadFile.size / 1024 / 1024).toFixed(2)}MB)`);
          }

          previewUrl = URL.createObjectURL(uploadFile);
          setTournamentGalleryPreviews((prev) => prev.concat(previewUrl));

          const fileBase64 = await fileToBase64(uploadFile);

          const uploadResponse = await fetch("/a/app?app=uploadFileToShopify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileBase64,
              fileName: uploadFile.name,
              mimeType,
            }),
          });
          const uploadData = await uploadResponse.json().catch(() => ({}));

          if (!uploadResponse.ok || uploadData?.success === false) {
            const message = uploadData?.message || uploadData?.error || `Upload failed (${uploadResponse.status})`;
            throw new Error(message);
          }

          const gid = uploadData?.gid || uploadData?.fileId || "";
          const url = uploadData?.url || "";
          if (!gid) {
            throw new Error("Upload completed but no file id was returned");
          }
          if (gid) setTournamentGalleryFileIds((prev) => prev.concat(gid));
          if (url) setTournamentGalleryUrls((prev) => prev.concat(url));
        } catch (error) {
          if (previewUrl) {
            setTournamentGalleryPreviews((prev) => prev.filter((value) => value !== previewUrl));
            if (String(previewUrl).startsWith("blob:")) URL.revokeObjectURL(previewUrl);
          }
          setTournamentSaveStatus(`Failed to upload ${file.name}: ${error?.message || "Unknown error"}`);
        } finally {
          setTournamentPendingUploads((prev) => Math.max(0, prev - 1));
        }
      }

      event.target.value = "";
    }

    function removeTournamentGalleryImage(index) {
      const previewToRevoke = tournamentGalleryPreviews[index];
      if (previewToRevoke && String(previewToRevoke).startsWith("blob:")) {
        URL.revokeObjectURL(previewToRevoke);
      }
      setTournamentGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
      setTournamentGalleryFileIds((prev) => prev.filter((_, i) => i !== index));
      setTournamentGalleryUrls((prev) => prev.filter((_, i) => i !== index));
    }

    function reorderTournamentGallery(fromIndex, toIndex) {
      if (fromIndex === toIndex) return;

      const reorder = (arr) => {
        const next = arr.slice();
        const moved = next.splice(fromIndex, 1)[0];
        next.splice(toIndex, 0, moved);
        return next;
      };

      setTournamentGalleryPreviews((prev) => reorder(prev));
      setTournamentGalleryFileIds((prev) => reorder(prev));
      setTournamentGalleryUrls((prev) => reorder(prev));
    }

    function closeTournamentModal() {
      setTournamentModalOpen(false);
      if (typeof onClose === "function") onClose();
    }

    async function submitTournamentForm() {
      if (/^saving\b/i.test(String(tournamentSaveStatus || ""))) {
        return;
      }

      const nextErrors = {};

      if (!tournamentProfileId) {
        nextErrors.profileId = "Select a location first.";
        setTournamentFieldErrors(nextErrors);
        setTournamentSaveStatus("Select a location first.");
        return;
      }

      const required = [
        { key: "title", label: "Title" },
        { key: "tournamentStartDate", label: "Start date" },
        { key: "locationName", label: "Location name" },
        { key: "ratingSystemType", label: "Rating system" },
        { key: "description", label: "Description" },
        { key: "buyIn", label: "Buy-in" },
      ];

      const missing = required.filter((field) => !tournamentForm[field.key]);
      if (missing.length > 0) {
        missing.forEach((field) => {
          nextErrors[field.key] = `${field.label} is required.`;
        });
        if (nextErrors.locationName) {
          nextErrors.profileId = nextErrors.locationName;
          delete nextErrors.locationName;
        }
        setTournamentFieldErrors(nextErrors);
        setTournamentSaveStatus(`Missing: ${missing.map((field) => field.label).join(", ")}`);
        return;
      }

      const normalizedContactEmail = normalizeOptionalEmail(tournamentForm.contactEmail);
      if (normalizedContactEmail && !isValidEmail(normalizedContactEmail)) {
        setTournamentFieldErrors({ contactEmail: "Organizer email must be a valid email address." });
        setTournamentSaveStatus("Organizer email must be a valid email address.");
        return;
      }

      if (tournamentPendingUploads > 0) {
        setTournamentFieldErrors({});
        setTournamentSaveStatus("Please wait for image uploads to finish.");
        return;
      }

      setTournamentFieldErrors({});

      const recurringEnabled = Boolean(
        tournamentForm.seriesEnabled || String(tournamentForm.series || "").trim()
      );
      const recurringCount = recurringEnabled
        ? Math.max(1, Math.min(52, Number.parseInt(String(tournamentForm.seriesCount || "1"), 10) || 1))
        : 1;
      const recurringInterval = Math.max(
        1,
        Number.parseInt(String(tournamentForm.seriesInterval || "1"), 10) || 1
      );
      const recurringDays = Array.isArray(tournamentForm.seriesDaysOfWeek)
        ? tournamentForm.seriesDaysOfWeek
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value >= 0 && value <= 6)
        : [];
      const recurringFrequency = String(tournamentForm.seriesFrequency || "weekly").trim().toLowerCase();

      const payload = {
        profileId: tournamentProfileId,
        title: tournamentForm.title,
        series: String(tournamentForm.series || "").trim(),
        locationName: tournamentForm.locationName,
        contactName: tournamentForm.contactName,
        contactPhone: tournamentForm.contactPhone,
        contactEmail: normalizedContactEmail,
        tournamentStartDate: datetimeLocalToIso(tournamentForm.tournamentStartDate),
        tournamentEndDate: datetimeLocalToIso(tournamentForm.tournamentEndDate || tournamentForm.tournamentStartDate),
        ratingSystemType: normalizeRatingSystemType(tournamentForm.ratingSystemType),
        rulesType: normalizeRulesType(tournamentForm.rulesType),
        buyIn: tournamentForm.buyIn,
        description: tournamentForm.description,
        galleryUrls: tournamentGalleryUrls,
        galleryFileIds: tournamentGalleryFileIds,
        specificationData: {
          ...(tournamentForm.specificationData || {}),
          tournaments: {
            ...((tournamentForm.specificationData || {}).tournaments || {}),
            recurring_event: recurringEnabled,
          },
        },
        seriesSettings: {
          enabled: recurringEnabled,
          count: recurringCount,
          frequency: ["daily", "weekly", "alternate", "monthly"].includes(recurringFrequency)
            ? recurringFrequency
            : "weekly",
          interval: recurringInterval,
          daysOfWeek: recurringDays,
        },
      };

      setTournamentSaveStatus("Saving...");

      try {
        const response = await fetch("/a/app?app=create-profile-tournament", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!data?.success) {
          throw new Error(data?.error || "Failed to create tournament");
        }

        setTournamentSaveStatus("Tournament created.");
        if (typeof onSubmit === "function") onSubmit(data);
      } catch (error) {
        setTournamentSaveStatus(error?.message || "Failed to create tournament");
      }
    }

    const ratingOptions = [
      { label: "APA", value: "apa_skill_level" },
      { label: "Fargo", value: "fargo_rate" },
      { label: "BCA", value: "bca_rating" },
      { label: "TAP", value: "tap_handicap" },
      { label: "USAPL", value: "usapl_rating" },
      { label: "In-house", value: "in_house_handicap" },
      { label: "Open", value: "no_handicap_open" },
      { label: "Other", value: "other" },
    ];

    const rulesOptions = [
      { label: "BCA", value: "bca_pool_league_rules" },
      { label: "APA", value: "apa_rules" },
      { label: "WEPF (International 8-Ball)", value: "wepf_world_rules" },
      { label: "WPA", value: "wpa_world_standardized_rules" },
      { label: "USAPL", value: "usapl_rules" },
      { label: "TAP", value: "tap_rules" },
      { label: "House Rules", value: "house_rules" },
      { label: "Other", value: "other" },
    ];

    const daysOfWeek = [
      { label: "Sun", value: 0 },
      { label: "Mon", value: 1 },
      { label: "Tue", value: 2 },
      { label: "Wed", value: 3 },
      { label: "Thu", value: 4 },
      { label: "Fri", value: 5 },
      { label: "Sat", value: 6 },
    ];

    const skillLevelOptions = [
      "Beginner",
      "Novice",
      "Intermediate",
      "Advanced",
      "Open",
      "Amateur",
      "Semi-Pro",
      "Pro",
      "Masters",
    ];

    if (typeof window.SharedTournamentModalRenderer !== "function") {
      return null;
    }

    return window.SharedTournamentModalRenderer({
      h,
      tournamentProfileId,
      setTournamentProfileId,
      tournamentLocationFormQuery,
      setTournamentLocationFormQuery: setTournamentLocationFormQueryWithSearch,
      tournamentLocationFormStateFilter,
      setTournamentLocationFormStateFilter: setTournamentLocationFormStateFilterWithSearch,
      tournamentLocationFormCityFilter,
      setTournamentLocationFormCityFilter: setTournamentLocationFormCityFilterWithSearch,
      tournamentForm,
      setTournamentForm,
      tournamentGalleryPreviews,
      tournamentPendingUploads,
      handleTournamentGallerySelect,
      removeTournamentGalleryImage,
      reorderTournamentGallery,
      tournamentSaveStatus,
      tournamentFieldErrors,
      setTournamentFieldErrors,
      submitTournamentForm,
      tournamentModalOpen,
      closeTournamentModal,
      tournamentStep,
      setTournamentStep,
      locationStateOptions,
      locationCityOptions,
      filteredProfiles,
      ratingOptions,
      rulesOptions,
      daysOfWeek,
      skillLevelOptions,
      getStateDisplayName,
      getStateAbbrev,
      normalizeRatingSystemType,
    });
  }

  window.renderFrontendTournamentForm = function renderFrontendTournamentForm(container, options) {
    if (!container) return;
    render(h(FrontendSharedTournamentModal, options || {}), container);
  };
})();
